import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import SearchIcon from '@mui/icons-material/Search';
import { Autocomplete, Box, Button, IconButton, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Context } from '../../service/Context';
import VirtualizedListbox from '../../utils/UtilesBuscadores';
import { capitalizar } from '../../utils/UtilesComun';
import { BadgeInfoApp, PaperApp } from '../custom/CustomComponents';
import CrearJugadorModal from '../jugador/CrearJugadorModal';

/**
 * Pestaña de búsqueda de jugadores para la pantalla unificada de exploración y búsqueda.
 */
const BuscadorJugadorTab = () => {
  const modalRef = useRef(null);
  const navigate = useNavigate();
  const { idUsuario, obtenerTodosJugadores, todosJugadores } = useContext(Context);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    async function cargarDatos() {
      if (idUsuario && (!todosJugadores || todosJugadores.length === 0)) {
        obtenerTodosJugadores();
      }
    }
    cargarDatos();
  }, [idUsuario, todosJugadores, obtenerTodosJugadores]);

  const clickJugador = (jugador) => {
    if (jugador !== null) {
      navigate(`/jugador/${jugador.idJugador}`);
    }
  };

  const normalizeStr = (str) =>
    (str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const customFilterOptions = (options, { inputValue: searchVal }) => {
    if (!searchVal) {
      return options;
    }

    const inputTerms = normalizeStr(searchVal)
      .split(' ')
      .filter((term) => term.length > 0);

    return options.filter((option) => {
      const normalizedLabel = normalizeStr(option.nombreJugador);
      return inputTerms.every((term) => normalizedLabel.includes(term));
    });
  };

  return (
    <Box>
      {/* Cabecera de la sección */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: '10px',
              backgroundColor: 'rgba(15, 118, 110, 0.08)',
              color: '#0f766e',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid rgba(15, 118, 110, 0.20)',
            }}
          >
            <PersonSearchIcon sx={{ fontSize: 24 }} />
          </Box>

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.25rem', sm: '1.45rem' },
                  color: 'text.primary',
                  lineHeight: 1.2,
                }}
              >
                Buscar Jugadores
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Botón Crear Jugador */}
        <Button
          onClick={() => modalRef.current?.handleOpen()}
          startIcon={<AddIcon sx={{ fontSize: '1.1rem !important' }} />}
          sx={{
            height: 36,
            px: 2,
            borderRadius: '8px',
            backgroundColor: '#0f766e',
            color: '#ffffff',
            fontSize: '0.82rem',
            fontWeight: 700,
            textTransform: 'none',
            boxShadow: '0 1px 3px rgba(15, 118, 110, 0.25)',
            '&:hover': {
              backgroundColor: '#0d9488',
              boxShadow: '0 2px 6px rgba(15, 118, 110, 0.35)',
            },
          }}
        >
          Crear jugador
        </Button>
      </Box>

      {/* Tarjeta principal con el buscador */}
      <PaperApp sx={{ p: { xs: 2.5, sm: 4 }, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box sx={{ width: '100%', maxWidth: 640 }}>
          <Typography sx={{ fontSize: '0.86rem', fontWeight: 600, color: '#334155', mb: 1 }}>
            Introduce el nombre o apellidos del jugador:
          </Typography>

          <Autocomplete
            id="buscador-jugadores-tab"
            fullWidth
            filterOptions={customFilterOptions}
            options={todosJugadores || []}
            ListboxComponent={VirtualizedListbox}
            getOptionLabel={(option) => capitalizar(option.nombreJugador || '')}
            onChange={(event, value) => clickJugador(value)}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
            clearOnBlur={false}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder=""
                variant="outlined"
                size="medium"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#0f766e', fontSize: '1.35rem', ml: 0.5 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <>
                      {inputValue ? (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setInputValue('')} sx={{ p: 0.25, mr: 0.5 }}>
                            <ClearIcon sx={{ fontSize: '1.05rem', color: '#94a3b8' }} />
                          </IconButton>
                        </InputAdornment>
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                  sx: {
                    borderRadius: '10px',
                    backgroundColor: '#f8fafc',
                    fontSize: '0.90rem',
                    fontWeight: 500,
                    '& fieldset': { borderColor: '#cbd5e1' },
                    '&:hover fieldset': { borderColor: '#0f766e' },
                    '&.Mui-focused': {
                      backgroundColor: '#ffffff',
                      '& fieldset': { borderColor: '#0f766e', borderWidth: 2 },
                    },
                  },
                }}
              />
            )}
            renderOption={(props, option) => [
              props,
              <Box key={option.idJugador} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
                <BadgeInfoApp>{option.ano || '?'}</BadgeInfoApp>
                <Typography sx={{ fontSize: '0.86rem', fontWeight: 600, color: '#1e293b' }}>
                  {capitalizar(option.nombreJugador)}
                </Typography>
              </Box>,
            ]}
            PaperComponent={(paperProps) => (
              <Paper
                {...paperProps}
                sx={{
                  mt: 1,
                  borderRadius: '10px',
                  border: '1px solid #cbd5e1',
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15)',
                  overflow: 'hidden',
                  backgroundColor: '#ffffff',
                }}
              />
            )}
          />

          <Typography sx={{ color: '#64748b', fontSize: '0.78rem', mt: 1.5, textAlign: 'center' }}>
            Selecciona un jugador para consultar su ficha técnica, informes de scouting y rendimiento deportivo.
          </Typography>
        </Box>
      </PaperApp>

      <CrearJugadorModal ref={modalRef} onUpdate={() => obtenerTodosJugadores()} />
    </Box>
  );
};

export default BuscadorJugadorTab;
